import { C1C_DEVICE_TYPES, DEVICE, DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import parsePhoneNumberFromString from 'libphonenumber-js';
import _ from 'lodash';

/**
 * Decides whether a phone number should get North American dash formatting based on its country. Tests the country code for membership in the US and CA list with lodash includes.
 *
 * @param country - Two-letter country code of the phone number.
 * @returns True when the country is US or CA.
 */
const checkToFormatPhoneNumber = (country: any) => {
  const shouldFormatPhoneNumberCountries = ['US', 'CA'];
  return _.includes(shouldFormatPhoneNumberCountries, country);
};

/**
 * Renders a ten-digit phone number in dashed 3-3-4 form for display. Applies the grouping regex only when the value is a string, returning an empty string for anything else.
 *
 * @param phoneNumber - Phone number to format.
 * @returns Dash-separated phone number, or an empty string for non-string input.
 */
const formatPhoneNumber = (phoneNumber: any) =>
  _.isString(phoneNumber) ? phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : '';

/**
 * Builds a one-line postal address for a facility, physician or patient contact for display.
 * Joins the present address, city and state with commas, optionally appends the country, and
 * falls back to the placeholder for a missing contact or an empty result.
 *
 * @param props - Options carrying contact, characterEmpty and includedCountry.
 * @returns The formatted address, or the placeholder.
 */
const generateFullAddress = ({
  contact,
  characterEmpty = DOUBLE_HYPHEN_BULLET,
  includedCountry = false,
}: any) => {
  if (!contact) {
    return characterEmpty;
  }

  const { address, city, state, country } = contact || {};
  let fullAddress: any = [];
  if (address) {
    fullAddress.push(address);
  }
  if (city) {
    fullAddress.push(city);
  }
  if (state) {
    fullAddress.push(state);
  }
  fullAddress = fullAddress.join(', ');
  if (includedCountry) {
    if (country) {
      fullAddress += ` ${country}`;
    }
  }
  return fullAddress || characterEmpty;
};

/**
 * Builds the display name of a patient, physician or user from their first and last name. Concatenates firstName and a space-prefixed lastName when present and falls back to the placeholder character for a missing record or an empty result.
 *
 * @param userInfo - Record holding firstName and lastName.
 * @param characterEmpty - Placeholder returned when no name is available.
 * @returns Concatenated full name, or the placeholder.
 */
const generateFullName = (userInfo: any, characterEmpty = DOUBLE_HYPHEN_BULLET) => {
  if (!userInfo) {
    return characterEmpty;
  }

  const { firstName, lastName } = userInfo;
  let fullName = '';
  if (firstName) {
    fullName += firstName;
  }
  if (lastName) {
    fullName += ` ${lastName}`;
  }
  return fullName || characterEmpty;
};

/**
 * Maps a legacy device model name onto the current marketing name shown in the UI. Returns undefined for a falsy input, translates BIOTRES to BIOCORE and BIOTRES_PRO to BIOCORE_PRO, and passes any other model through unchanged.
 *
 * @param oldDeviceModel - Legacy device model identifier.
 * @returns Current model name, the input when no mapping applies, or undefined.
 */
const convertDeviceModel = (oldDeviceModel: any) => {
  if (!oldDeviceModel) {
    return undefined;
  }

  switch (oldDeviceModel) {
    case DEVICE.BIOTRES: {
      return DEVICE.BIOCORE;
    }
    case DEVICE.BIOTRES_PRO: {
      return DEVICE.BIOCORE_PRO;
    }
    default: {
      return oldDeviceModel;
    }
  }
};

/**
 * Returns the display label for a CardioOne-supplied study device type.
 *
 * @param deviceType - Study-level device type.
 * @returns The device type when it is LifeSignals or VivaLNK, otherwise null.
 */
const formatC1cDeviceLabel = (deviceType: string | null | undefined) =>
  deviceType && (C1C_DEVICE_TYPES as readonly string[]).includes(deviceType) ? deviceType : null;

/**
 * Maps a current device model name back to the legacy identifier the backend stores. Returns undefined for a falsy input, translates BIOCORE to BIOTRES and BIOCORE_PRO to BIOTRES_PRO, and passes any other model through unchanged.
 *
 * @param newDeviceModel - Current device model name.
 * @returns Legacy model identifier, the input when no mapping applies, or undefined.
 */
const revertDeviceModel = (newDeviceModel: any) => {
  if (!newDeviceModel) {
    return undefined;
  }

  switch (newDeviceModel) {
    case DEVICE.BIOCORE: {
      return DEVICE.BIOTRES;
    }
    case DEVICE.BIOCORE_PRO: {
      return DEVICE.BIOTRES_PRO;
    }
    default: {
      return newDeviceModel;
    }
  }
};

/**
 * Picks which diagnosis code of a study diagnosis to display. Returns the primary code when the primary flag is set and the second code otherwise.
 *
 * @param diagnosis - Diagnosis object holding primary and second codes.
 * @param isPrimaryCode - Flag selecting the primary code over the secondary one.
 * @returns The selected diagnosis code.
 */
const formatDiagnosisCode = (diagnosis: any, isPrimaryCode = false) => {
  if (isPrimaryCode) {
    return diagnosis.primary;
  }

  return diagnosis.second;
};

/**
 * Renders a non North American phone number in international form with a parenthesised dial code. Parses the number for the given country with parsePhoneNumberFromString, formats it internationally, then rewrites the leading '+' to '(+' and the first space to ') ', returning the original number when parsing yields nothing.
 *
 * @param phoneNumber - Raw phone number to format.
 * @param countryCode - Two-letter country code used to parse the number.
 * @returns Formatted international number, or the input when it could not be parsed.
 */
const formatOtherPhoneNumber = (phoneNumber: any, countryCode: any) => {
  const formattedPhoneNumber = parsePhoneNumberFromString(
    phoneNumber,
    countryCode,
  )?.formatInternational();
  return formattedPhoneNumber?.replace('+', '(+')?.replace(' ', ') ') || phoneNumber;
};

/**
 * Renders a US or Canadian phone number as '(+1) 000-000-0000'. Strips every non-digit, drops a leading country digit from an eleven-digit value, and returns the input untouched unless exactly ten national digits remain, which are then grouped 3-3-4.
 *
 * @param phoneNumber - Raw phone number to format.
 * @returns Formatted +1 number, or the input when it is not a ten-digit national number.
 */
const formatUSCAPhoneNumber = (phoneNumber: any) => {
  const digits = _.replace(phoneNumber, /\D/g, '');
  const nationalNumber = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (nationalNumber.length !== 10) {
    return phoneNumber;
  }

  return `(+1) ${_.replace(nationalNumber, /(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`;
};

/**
 * Formats a stored phone number for display according to its country. Returns an empty string without a number and the raw number without a country code, routes US and CA numbers through the '(+1) 000-000-0000' formatter, and sends every other country to the parenthesised international formatter.
 *
 * @param phoneNumber - Phone number to format.
 * @param countryCode - Two-letter country code driving the formatting rule.
 * @returns Country-appropriate formatted number, the raw number, or an empty string.
 */
const formatPhoneNumberWithCode = (phoneNumber: any, countryCode?: any) => {
  if (!phoneNumber) {
    return '';
  }
  if (!countryCode) {
    return phoneNumber;
  }
  if (countryCode === 'US' || countryCode === 'CA') {
    return formatUSCAPhoneNumber(phoneNumber);
  }
  return formatOtherPhoneNumber(phoneNumber, countryCode);
};

/**
 * Formats a phone number as the user types it, keeping partial input usable. Strips existing dashes and, only for countries that need North American grouping, returns the digits untouched below four, as 3-remainder below seven, and as 3-3-4 otherwise; other countries get the dash-stripped digits and a falsy input yields null.
 *
 * @param phone - Phone number text being entered.
 * @param country - Two-letter country code deciding whether grouping applies.
 * @returns Progressively grouped phone string, the bare digits, or null.
 */
const phoneFormat = (phone: any, country?: any) => {
  if (!phone) {
    return null;
  }

  const rawPhone = phone.replaceAll('-', '');
  if (checkToFormatPhoneNumber(country)) {
    const { length } = rawPhone;
    if (length < 4) {
      return rawPhone;
    }
    if (length < 7) {
      return `${rawPhone.slice(0, 3)}-${rawPhone.slice(3)}`;
    }
    return `${rawPhone.slice(0, 3)}-${rawPhone.slice(3, 6)}-${rawPhone.slice(6, 10)}`;
  }

  return rawPhone;
};

/**
 * Left-pads a study, report or event friendly id with zeros to a fixed width for display. Pads the stringified number when it is present or exactly zero and returns an empty string for any other falsy value.
 *
 * @param num - Identifier value to pad.
 * @param places - Total width of the padded result.
 * @returns Zero-padded identifier string, or an empty string when no value was given.
 */
const zeroPad = (num: any, places = 5) => {
  if (num || num === 0) {
    return num.toString().padStart(places, '0');
  }

  return '';
};

/**
 * Generates a random lowercase-letter identifier for DOM ids and table keys. Appends the requested number of characters, each drawn from the a-z alphabet at a Math.random index.
 *
 * @param length - Number of characters to generate.
 * @returns Random lowercase identifier of the requested length.
 */
const makeId = (length: any) => {
  let result = '';
  const characters = 'abcdefghijklmnopqrstuvwxyz';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

/**
 * Swaps the keys and values of a lookup map so a code can be resolved back to its label. Maps every entry through Object.entries reversed and rebuilds an object with Object.fromEntries.
 *
 * @param obj - Object whose keys and values are exchanged.
 * @returns New object keyed by the original values.
 */
const invertObject = (obj: any) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));

export {
  checkToFormatPhoneNumber,
  convertDeviceModel,
  formatC1cDeviceLabel,
  formatDiagnosisCode,
  formatPhoneNumber,
  generateFullAddress,
  generateFullName,
  invertObject,
  makeId,
  phoneFormat,
  revertDeviceModel,
  formatPhoneNumberWithCode,
  zeroPad,
};
